import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useAppShell, useSidebarPlacement } from "./ShellContext.js";
import { renderShellLink } from "./links.js";
const Sidebar = React.forwardRef(function Sidebar2({ brand, groups, footer, label = "Navigation", navLabel = "Main", renderLink, className, ...rest }, ref) {
  const shell = useAppShell();
  const placement = useSidebarPlacement();
  const baseId = React.useId();
  const linkRenderer = renderLink ?? shell?.renderLink;
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    brand != null && /* @__PURE__ */ jsx("div", { className: "ev-sidebar__brand", children: brand }),
    /* @__PURE__ */ jsx("nav", { className: "ev-sidebar__nav", "aria-label": navLabel, children: groups.map((group, groupIndex) => {
      const headingId = group.label != null ? `${baseId}-g${groupIndex}` : void 0;
      return /* @__PURE__ */ jsxs(React.Fragment, { children: [
        group.divider && /* @__PURE__ */ jsx("div", { className: "ev-nav-sep", role: "separator" }),
        hasAction(group.action) ? (
          // The label keeps the id that names the list; the action is its sibling, so it never joins that name.
          /* @__PURE__ */ jsxs("div", { className: "ev-nav-group ev-nav-group--with-action", children: [
            headingId && /* @__PURE__ */ jsx("div", { className: "ev-nav-group__label", id: headingId, children: group.label }),
            /* @__PURE__ */ jsx("div", { className: "ev-nav-group__action", children: group.action })
          ] })
        ) : headingId && /* @__PURE__ */ jsx("div", { className: "ev-nav-group", id: headingId, children: group.label }),
        /* @__PURE__ */ jsx("ul", { className: "ev-nav-list", role: "list", "aria-labelledby": headingId, children: group.items.map((item) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(SidebarEntry, { item, renderLink: linkRenderer }) }, item.id)) })
      ] }, group.id ?? groupIndex);
    }) }),
    footer != null && /* @__PURE__ */ jsx("div", { className: "ev-sidebar__foot", children: footer })
  ] });
  if (placement === "drawer") return content;
  return /* @__PURE__ */ jsx("aside", { ref, className: cx("ev-sidebar", className), "aria-label": label, ...rest, children: content });
});
function hasAction(action) {
  return action != null && typeof action !== "boolean" && action !== "";
}
function SidebarEntry({ item, renderLink }) {
  const inner = /* @__PURE__ */ jsxs(Fragment, { children: [
    item.icon != null && /* @__PURE__ */ jsx("span", { className: "ev-nav-item__icon", "aria-hidden": "true", children: item.icon }),
    /* @__PURE__ */ jsx("span", { className: "ev-nav-item__label", children: item.label }),
    item.count != null && /* @__PURE__ */ jsxs(Fragment, { children: [
      " ",
      /* @__PURE__ */ jsx("span", { className: "ev-nav-item__count", "aria-hidden": item.countLabel ? true : void 0, children: item.count }),
      item.countLabel && /* @__PURE__ */ jsx("span", { className: "ev-visually-hidden", children: item.countLabel })
    ] })
  ] });
  if (item.href != null) {
    return renderShellLink(
      renderLink,
      { href: item.href, className: "ev-nav-item", "aria-current": item.current ? "page" : void 0, children: inner },
      { id: item.id, href: item.href }
    );
  }
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      className: "ev-nav-item",
      "aria-current": item.current ? "page" : void 0,
      "data-ev-drawer-close": "",
      onClick: item.onSelect,
      children: inner
    }
  );
}
function SidebarBrand({ name, subtitle, tile }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    tile != null && /* @__PURE__ */ jsx("span", { className: "ev-app-tile", "aria-hidden": "true", children: tile }),
    /* @__PURE__ */ jsxs("span", { className: "ev-sidebar__brand-text", children: [
      /* @__PURE__ */ jsx("b", { children: name }),
      subtitle != null && /* @__PURE__ */ jsx("small", { children: subtitle })
    ] })
  ] });
}
export {
  Sidebar,
  SidebarBrand
};
//# sourceMappingURL=Sidebar.js.map
